/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  empty,
  errorResponse,
  floatval,
  isArray,
  isString,
  prepareResponseData,
  reIndex,
} from "../../Utilities/utils";
import { Toast } from "primereact/toast";
import { isObject, toUpper } from "lodash";
import * as Yup from "yup";

// css
import "../students/Students.css";
import "../dashboard/Dashboard.css";
import "../staff/Staff.css";

// api
import studentApi from "../../api/Student";
import subjectApi from "../../api/Subject";
import paymentApi from "../../api/Payment";
import termApi from "../../api/Terms";
import sessionApi from "../../api/Session";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import { FaExclamationTriangle, FaPrint } from "react-icons/fa";
import { useContext } from "react";
import { AuthContext } from "../Root/ProtectedRoute";
import FullPageLoader from "../../components/loader/FullPageLoader";
import colors from "../../config/colors";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Form, Formik } from "formik";
import EmptyListResponse from "../../components/emptyDiv/EmptyListResponse";
import MultiSelectField from "../../components/form/MultiSelectField";

const printReport = () => {
  window.print();
};

const validationSchema = Yup.object().shape({
  session_ids: Yup.array().optional(),
  term_ids: Yup.array().optional(),
});


const initialValues = { session_ids: [], term_ids: [] };

const Transcript = ({ ...props }) => {
  const printRef = useRef();
  const { user } = useContext(AuthContext);
  const [studentData, setStudentData] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [paymentRecords, setPaymentRecords] = useState([]);
  const [assessmentRecords, setAssessmentRecords] = useState([]);
  const [activationModalOpen, setActivationModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [termSession, setTermSession] = useState({ sessionId: '', termId: '' })
  const [ actionDone, setActionDone] = useState(0);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { studentId } = useParams();
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (!studentId) {
      navigate("/404");
    }
    getStudentDetails();
    getSubjects();
  }, []);

  // function to get all terms
  const getTerms = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await termApi.getTerms(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return [];
      } else {
        const data =
          !empty(response_data) && isArray(response_data.response)
            ? response_data.response
            : [];
        setTerms(data);
        return data;
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
      return [];
    }
  };

  // function to get all sessions
  const getSessions = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await sessionApi.getSessions(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return [];
      } else {
        const data =
          !empty(response_data) && isArray(response_data.response)
            ? response_data.response
            : [];
        setSessions(data);
        return data;
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
      return [];
    }
  };

  /**
   * Get Student Payments
   * @returns 
   */
  const getPaymentRecord = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await paymentApi.getPaymentRecord(schoolId, studentId, "ACCOUNT ACTIVATION", "COMPLETED");
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return [];
      } else {
        const data =
          !empty(response_data) && isArray(response_data.response)
            ? response_data.response
            : [];
        setPaymentRecords(data);
        return data;
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
      return [];
    }
  };

  // get subjects
  const getSubjects = async () => {
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await subjectApi.getSubjects(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setSubjects([]);
      } else {
        const data =
          !empty(response_data) &&
          !empty(response_data.response) &&
          isArray(response_data.response)
            ? response_data.response
            : [];
        const reIndexedSubjects = !empty(data) ? reIndex(data) : {};
        return setSubjects(reIndexedSubjects);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
      return [];
    }
  };

  const getStudentDetails = async (selectedSessionIds = [], selectedTermIds = []) => {
    setIsLoading(true);
    try {
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      let response = {};
      let response_data = {};
      let studentDetails = {};
      if (empty(studentData)) {
        response = await studentApi.getStudentProfile(studentId, schoolId);
        response_data = prepareResponseData(response);
        if (empty(response_data) || !response_data.success) {
          navigate(-1);
          return responseDialog(
            "error",
            "Error Alert",
            !empty(response_data.response)
              ? typeof response_data.response === "string"
                ? response_data.response
                : "Something went wrong!"
              : "Failed to fetch student details"
          );
        }

        studentDetails =
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : {};
      } else {
        studentDetails = studentData;
      }

      let newTerms = [];
      if (empty(terms)) {
        newTerms = await getTerms();
      } else {
        newTerms = terms;
      }

      let newPaymentRecords = [];
      if (empty(paymentRecords)) {
        newPaymentRecords = await getPaymentRecord();
      } else {
        newPaymentRecords = paymentRecords;
      }
      const reIndexedTerms =
        !empty(newTerms) && isArray(newTerms) ? reIndex(newTerms, "_id") : {};
      
      let newSessions = [];
      if (empty(newSessions)) {
        newSessions = await getSessions();
      } else {
        newSessions = sessions;
      }

      const reIndexedSessions =
        !empty(newSessions) && isArray(newSessions) ? reIndex(newSessions, "_id") : {};
      const assessmentRecords =
        !empty(studentDetails) && !empty(studentDetails.assessment)
          ? studentDetails.assessment
          : [];
      
      // eslint-disable-next-line array-callback-return
      const transcript = assessmentRecords.reduce((acc, item) => {
        if (isObject(item)) {
          const { sessionId, termId } = item;

          // a session and a term is selected
          if (!empty(selectedSessionIds) && selectedSessionIds.includes(sessionId) && !empty(selectedTermIds) && selectedTermIds.includes(termId)) {
            // Check if the sessionId exists, if not create a new session group
            if (!acc[sessionId]) {
              acc[sessionId] = {};
            }
  
            // Check if the termId exists under the sessionId, if not create a new term group
            if (!acc?.[sessionId]?.[termId]) {
              acc[sessionId][termId] = [];
            }
  
            // Push the item into the term group
            acc[sessionId][termId].push(item);
  
            return acc;
          }

          // a session only is selected
          if (!empty(selectedSessionIds) && selectedSessionIds.includes(sessionId) && empty(selectedTermIds)) {
            // Check if the sessionId exists, if not create a new session group
            if (!acc[sessionId]) {
              acc[sessionId] = {};
            }
  
            // Check if the termId exists under the sessionId, if not create a new term group
            if (!acc?.[sessionId]?.[termId]) {
              acc[sessionId][termId] = [];
            }
  
            // Push the item into the term group
            acc[sessionId][termId].push(item);
  
            return acc;
          }

          // session is empty but term is not empty
          if (!empty(selectedTermIds) && selectedTermIds.includes(termId) && empty(selectedSessionIds)) {
            // Check if the sessionId exists, if not create a new session group
            if (!acc[sessionId]) {
              acc[sessionId] = {};
            }
  
            // Check if the termId exists under the sessionId, if not create a new term group
            if (!acc?.[sessionId]?.[termId]) {
              acc[sessionId][termId] = [];
            }
  
            // Push the item into the term group
            acc[sessionId][termId].push(item);
  
            return acc;
          }

          // default, session and term are empty
          if (empty(selectedSessionIds) && empty(selectedTermIds)) {
            // Check if the sessionId exists, if not create a new session group
            if (!acc[sessionId]) {
              acc[sessionId] = {};
            }
  
            // Check if the termId exists under the sessionId, if not create a new term group
            if (!acc?.[sessionId]?.[termId]) {
              acc[sessionId][termId] = [];
            }
  
            // Push the item into the term group
            acc[sessionId][termId].push(item);
  
            return acc;
          }

          return acc;
        }
      }, {});

      if (!isObject(transcript)) {
        setAssessmentRecords([]);
        return responseDialog("error", "No Response", "There are no assessment for the selected period(s).");
      }
      const groupedTranscript = await Object.entries(transcript).map(
        ([sessionId, terms]) => {
          const sessionTitle =
            !empty(reIndexedSessions) &&
            !empty(reIndexedSessions[sessionId]) &&
            !empty(reIndexedSessions[sessionId].title)
              ? reIndexedSessions[sessionId].title
              : "";
          const date = reIndexedSessions?.[sessionId]?.dateCreated ? new Date(reIndexedSessions?.[sessionId]?.dateCreated ).getTime() : new Date().getTime();
          return {
            index: date,
            sessionId,
            sessionTitle,
            terms: Object.entries(terms).map(([termId, assessments]) => {
              let paid = false;
              for (let i = 0; i < newPaymentRecords.length; i++) {
                paid = false;
                const data = newPaymentRecords?.[i] || {};
                if (data?.sessionId === sessionId && data?.termId === termId) {
                  paid = true;
                  break;
                }
              }
              return {
                index: `${date}-${sessionId}-${termId}`,
                termId,
                termTitle:
                  !empty(reIndexedTerms) &&
                    !empty(reIndexedTerms[termId]) &&
                    !empty(reIndexedTerms[termId].title)
                    ? reIndexedTerms[termId].title
                    : "",
                assessments,
                paid
              }
            }),
          };
        }
      );

      setAssessmentRecords(isArray(groupedTranscript) ? groupedTranscript : []);
      if (!empty(studentDetails)) {
        const firstName = !empty(studentDetails.firstName)
          ? studentDetails.firstName
          : "";
        const lastName = !empty(studentDetails.lastName)
          ? studentDetails.lastName
          : "";
        const middleName = !empty(studentDetails.middleName)
          ? studentDetails.middleName
          : "";
        if (empty(studentData)) {
          setStudentData({
            _id: !empty(studentDetails._id) ? studentDetails._id : "",
            firstName,
            lastName,
            middleName,
            fullName: toUpper(lastName) + ", " + firstName + " " + middleName,
            gender: !empty(studentDetails.gender) ? studentDetails.gender : "",
            regNo: !empty(studentDetails.regNo) ? studentDetails.regNo : "",
            email: !empty(studentDetails.email) ? studentDetails.email : "",
            hasPaid:
              !empty(studentDetails.paid) &&
              isString(studentDetails.paid) &&
              studentDetails.paid.toLowerCase() === "yes"
                ? true
                : false,
            graduated: !empty(studentDetails.graduationClassId) ? true : false,
            graduationYear: !empty(studentDetails.graduationYear)
              ? studentDetails.graduationYear
              : "Active Student",
            assessment: studentDetails?.assessment || []
          });
        }
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  // hide activation modal
  const hideActivationModal = () => {
    setActivationModalOpen(false);
  };

  //function to activate student on payment
  const activateStudent = async ({ sessionId, termId }) => {
    try {
      if (activationModalOpen) setActivationModalOpen(false);
      if (!isActionLoading) setIsActionLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const studentId =
        !empty(studentData) && !empty(studentData._id) ? studentData._id : "";
      const response = await studentApi.activateStudent([studentId], schoolId, sessionId, termId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        const error_response =
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : `Failed to activate student`;
        return responseDialog("error", "Error Alert", error_response);
      }

      await getStudentDetails();
      setActionDone(new Date().getTime());
      return responseDialog(
        "success",
        "Success",
        `Activated student successfully!`
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsActionLoading(false);
    }
  };

  const activateStudentDialogFooter = (
    <div
      style={{
        marginTop: 20,
      }}
    >
      <Button
        label="No"
        icon="pi pi-times"
        style={{
          backgroundColor: "transparent",
          color: colors.danger,
          borderColor: colors.danger,
          borderWidth: 1,
        }}
        onClick={hideActivationModal}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        style={{
          backgroundColor: "transparent",
          color: colors.success,
          borderColor: colors.success,
          borderWidth: 1,
        }}
        onClick={() => {
          activateStudent(termSession);
        }}
      />
    </div>
  );

  const handleSubmit = async (values) => {
    try {
      if (empty(values)) {
        return responseDialog("error", "Something went wrong.", "Please refresh or try again later.");
      }
      const sessionIds = values?.session_ids || "";
      const termIds = values?.term_ids || "";

      await getStudentDetails(sessionIds, termIds);
      
      
    } catch (error) {
      responseDialog("error", "Error Alert", "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiSelectChange = async (name, newValue, setFieldValue) => {
    const inputValues = !empty(newValue) && isArray(newValue) ? newValue : [];
    setFieldValue(name, inputValues);
  }

  return (
    <>
      <AppWrapper {...props}>
        <main>
          {/* menu header */}
          <MainHeader title="Student Transcript" />
          <div className="mb-40">
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form style={{ width: "100%" }}>
                  <div className="app_input_group">
                    <MultiSelectField
                      labelTitle="Session"
                      placeholder="Select Session"
                      name="session_id"
                      options={sessions}
                      selectedOptions={values.session_ids}
                      required={true}
                      height={50}
                      mt="mt-10"
                      valueKey="id"
                      containerWidth={250}
                      onChange={(e) =>
                        handleMultiSelectChange(
                          "session_ids",
                          e.value,
                          setFieldValue
                        )
                      }
                    />
                    <MultiSelectField
                      labelTitle="Term"
                      placeholder="Select Term"
                      name="term_id"
                      options={terms}
                      selectedOptions={values.term_ids}
                      required={true}
                      height={50}
                      mt="mt-10"
                      valueKey="id"
                      containerWidth={250}
                      onChange={(e) =>
                        handleMultiSelectChange(
                          "term_ids",
                          e.value,
                          setFieldValue
                        )
                      }
                    />
                    <ButtonIcon
                      height={45}
                      marginTop={5}
                      color={colors.white}
                      backgroundColor={colors.primary}
                      width={160}
                      borderColor={colors.primary}
                      buttonText="Submit"
                      type="submit"
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <div className="report-sheet-contents-display" key={actionDone}>
            {!isLoading && (studentData?.hasPaid ||
            studentData?.graduated) ? (
              <>
                <ButtonIcon
                  buttonText="Print"
                  icon={<FaPrint />}
                  marginTop={3}
                  iconMarginRight={8}
                  backgroundColor="transparent"
                  color={colors.primary}
                  borderColor={colors.primary}
                  height={30}
                  width={150}
                  onClick={() => printReport()}
                />
                <div className="transcript-container-script mt-20">
                  <div
                    className="transcript-display-script"
                    ref={printRef}
                  >
                    <div className="report-header">
                      <div style={{ width: 100, height: 100 }}>
                        <img
                          src={
                            !empty(user) && !empty(user.avatar)
                              ? user.avatar
                              : ""
                          }
                          alt="non"
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                      <div>
                        <div className="report-title">
                          {!empty(user) && !empty(user.name) ? user.name : "NA"}
                        </div>
                        <div className="report-subtitle">
                          {!empty(user) && !empty(user.address)
                            ? user.address
                            : "NA"}
                        </div>
                        <div className="report-subtitle fs-14">
                          {`TEL.: ${
                            !empty(user) && !empty(user.phoneNumber1)
                              ? user.phoneNumber1 + " ,"
                              : ""
                          }${
                            !empty(user) && !empty(user.phoneNumber2)
                              ? user.phoneNumber2 + " ,"
                              : ""
                          }${
                            !empty(user) && !empty(user.ownerPhone)
                              ? user.ownerPhone
                              : ""
                          }`}
                        </div>
                        <div className="report-subtitle fs-14 mb-10">
                          {`Email: ${
                            !empty(user) && !empty(user.email)
                              ? user.email
                              : "---"
                          }`}
                        </div>
                      </div>
                      <div style={{ width: 100, height: 100 }}>
                        <img
                          src={
                            !empty(user) && !empty(user.avatar)
                              ? user.avatar
                              : ""
                          }
                          alt="none"
                          style={{ width: "100%", height: "100%" }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="transcript-student-data mt-30 mb-30">
                        <div className="transcript-student-details">
                          <div className="mt-5">
                            <strong>Student Name: </strong>{" "}
                            {studentData?.fullName || ""}
                          </div>
                          <div className="mt-5">
                            <strong>Reg. No.: </strong>{" "}
                            {!empty(studentData.regNo) ? studentData.regNo : ""}
                          </div>
                          <div className="mt-5">
                            <strong>Gender: </strong>{" "}
                            {!empty(studentData.gender)
                              ? studentData.gender
                              : ""}
                          </div>
                          <div className="mt-5">
                            <strong>Email: </strong>{" "}
                            {!empty(studentData.email) ? studentData.email : ""}
                          </div>
                          { studentData.graduationYear.toLowerCase() !== 'active student' && <div className="mt-5">
                            <strong>Graduation Year: </strong>{" "}
                            {!empty(studentData.graduationYear)
                              ? studentData.graduationYear
                              : ""}
                          </div>}
                        </div>
                      </div>
                      {/* end menu header */}
                      {!isLoading && (studentData?.hasPaid || studentData?.graduated) ? (
                        <div style={{ width: "100%", marginTop: 10 }}>
                          <div>
                            {isArray(assessmentRecords) && !empty(assessmentRecords) ? (
                                assessmentRecords.map((session, xindex) => {
                                  return (
                                    <div key={session?.index + `-${xindex}`}>
                                    {/* Loop through each term in the session */}
                                    {isArray(session?.terms)
                                      ? session.terms.map((term, idx) => {
                                          const termTitle = term?.termTitle
                                              || "";
                                          const sessionTitle = session?.sessionTitle
                                              || "";
                                          return (
                                            <>
                                              <div key={term?.index + `-${idx}`}>
                                                {/* Display the term row */}
                                                <p
                                                  style={{
                                                    marginTop: 10,
                                                    marginBottom: 10,
                                                    width: 500,
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  <strong>{`${termTitle}, ${sessionTitle} Academic Session`}</strong>
                                                </p>
                                                { term?.paid ?
                                                  <table className="transcript-box">
                                                    <thead>
                                                      <tr>
                                                        <th>Subject</th>
                                                        {
                                                          Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                                            <th>CA{idx + 1}</th>
                                                          ))
                                                        }
                                                        <th>Exam</th>
                                                        <th>Total</th>
                                                        <th>Grade</th>
                                                        <th>Remarks</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {/* Loop through each assessment in the term */}
                                                      {!empty(term) &&
                                                      isObject(term) &&
                                                      !empty(term.assessments) &&
                                                      isArray(term.assessments)
                                                        ? term.assessments.map(
                                                            (assessment, index) => {
                                                              if (
                                                                !empty(assessment)
                                                              ) {
                                                                const subjectId =
                                                                  !empty(
                                                                    assessment.subjectId
                                                                  )
                                                                    ? assessment.subjectId
                                                                    : "";
                                                                const ca1 = floatval(assessment.ca1) || 0;
                                                                const ca2 = floatval(assessment.ca2) || 0;
                                                                const ca3 = floatval(assessment.ca3) || 0;  
                                                                const exam = floatval(assessment.exam) || 0;
                                                                const total = floatval(assessment.total) || floatval(
                                                                        ca1 +
                                                                          ca2 +
                                                                          ca3 +
                                                                          exam
                                                                      ) || 0;
                                                                const grade =
                                                                  !empty(
                                                                    assessment.grade
                                                                  )
                                                                    ? assessment.grade
                                                                    : "---";
                                                                const remark =
                                                                  !empty(
                                                                    assessment.remark
                                                                  )
                                                                    ? assessment.remark
                                                                    : "---";
                                                                return (
                                                                  <tr key={subjectId}>
                                                                    <td>
                                                                      <strong>
                                                                        {!empty(
                                                                          subjects
                                                                        ) &&
                                                                        isObject(
                                                                          subjects
                                                                        ) &&
                                                                        !empty(
                                                                          subjects[
                                                                            subjectId
                                                                          ]
                                                                        ) &&
                                                                        !empty(
                                                                          subjects[
                                                                            subjectId
                                                                          ].title
                                                                        )
                                                                          ? toUpper(
                                                                              subjects[
                                                                                subjectId
                                                                              ]
                                                                                .title
                                                                            )
                                                                          : "N/A"}
                                                                      </strong>
                                                                    </td>
                                                                    {
                                                                      Array.from({ length: user?.noOfCAs || 2 }, (_, idx) => (
                                                                        <td>{assessment?.[`ca${idx + 1}`]}</td>
                                                                      ))
                                                                    }
                                                                    <td>{exam}</td>
                                                                    <td>{total}</td>
                                                                    <td>{grade}</td>
                                                                    <td>
                                                                      {toUpper(
                                                                        remark
                                                                      )}
                                                                    </td>
                                                                  </tr>
                                                                );
                                                              }
                                                              return null;
                                                            }
                                                          )
                                                        : 
                                                          null
                                                        }
                                                    </tbody>
                                                  </table>
                                                    : 
                                                    <div className="mt-50 assessment_not_paid">
                                                        <FaExclamationTriangle color="red" size={25} />
                                                        <div className="mt-5 fs-16">
                                                          <strong>Result Sheet for this term is unavailable</strong>
                                                        </div>
                                                        <div>
                                                          <ButtonIcon
                                                            height={45}
                                                            color={colors.primary}
                                                            backgroundColor="transparent"
                                                            width={220}
                                                            fontWeight="bold"
                                                            borderColor="transparent"
                                                            buttonText="Activate Payment"
                                                            onClick={() => {
                                                              setTermSession({ sessionId: session?.sessionId, termId: term?.termId })
                                                              return setActivationModalOpen(true);
                                                            }}
                                                          />
                                                        </div>
                                                      </div>
                                                  }
                                              </div>
                                            </>
                                            
                                          );
                                        })
                                      : 
                                        null
                                      }
                                  </div>
                                );
                              })
                            ) : 
                              <EmptyListResponse message="Sorry, there are no assessment record(s) for the selected period." />
                            }
                          </div>
                        </div>
                      ) : (
                        <div className="mt-50 assessment_not_paid">
                          <FaExclamationTriangle color="red" size={25} />
                          <p className="mt-10 fs-18 mb-5">
                            <strong>Result Sheet Unavailable</strong>
                          </p>
                          <p>This student has an outstanding portal levy</p>
                          <div>
                            <ButtonIcon
                              height={45}
                              color={colors.primary}
                              backgroundColor="transparent"
                              width={220}
                              fontWeight="bold"
                              borderColor="transparent"
                              buttonText="Activate Student"
                              onClick={() => setActivationModalOpen(true)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : <EmptyListResponse />}
          </div>
        </main>
        <Toast ref={toastTR} position="bottom-left" />
        {isLoading && <FullPageLoader visible={isLoading} />}
      </AppWrapper>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />

      <Dialog
        visible={activationModalOpen}
        style={{ width: "32rem", zIndex: 999999 }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={activateStudentDialogFooter}
        onHide={hideActivationModal}
      >
        <div
          className="confirmation-content"
          style={{ marginTop: 20, display: "flex", alignItems: "center" }}
        >
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem", marginRight: 15, color: colors.danger }}
          />
          {
            <>
              <p>
                Are you sure you want to <strong>activate</strong> this student?{" "}
                <br />
                <br />
                By clicking "Yes", you are confirming that{" "}
                <strong>
                  {studentData?.fullName || ""}
                </strong>{" "}
                made portal payment for this term.
                <br />
                <br />
                <em style={{ color: colors.danger }}>
                  <strong>Note:</strong> This action cannot be undone.
                </em>
              </p>
            </>
          }
        </div>
      </Dialog>
    </>
  );
};

export default Transcript;
